import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Outlet } from '@tanstack/react-router'
import { AppSidebar } from '@/components/layout/app-sidebar'
import { AppTopbar } from '@/components/layout/app-topbar'
import { workspaceQueryOptions } from '@/lib/workspace'

/**
 * The workspace lives in the URL rather than in a cookie, so deep links work
 * and two tabs on two workspaces cannot corrupt each other.
 *
 * Membership is verified server-side on every request: the endpoint answers 404
 * for an unknown slug and for one the caller does not belong to, so this loader
 * never has to distinguish them.
 */
export const Route = createFileRoute('/_auth/w/$workspaceSlug')({
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(workspaceQueryOptions(params.workspaceSlug)),
  component: WorkspaceLayout,
})

const weddingWeekdayFormatter = new Intl.DateTimeFormat('cs-CZ', {
  weekday: 'long',
})

const weddingDateFormatter = new Intl.DateTimeFormat('cs-CZ', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
})

function formatWeddingDate(value?: string | null) {
  if (!value) {
    return undefined
  }

  const date = new Date(`${value}T00:00:00`)
  const weekday = weddingWeekdayFormatter.format(date)
  const capitalizedWeekday = weekday.charAt(0).toLocaleUpperCase('cs-CZ') + weekday.slice(1)

  return `${capitalizedWeekday}, ${weddingDateFormatter.format(date)}`
}

function WorkspaceLayout() {
  const { workspaceSlug } = Route.useParams()
  const workspaceQuery = useQuery(workspaceQueryOptions(workspaceSlug))
  const weddingName =
    workspaceQuery.data?.wedding?.name ?? workspaceQuery.data?.workspace.name ?? 'Svatba'
  const weddingDate = workspaceQuery.data?.wedding?.weddingDate ?? null

  return (
    <div className="flex h-dvh overflow-hidden bg-background">
      <AppSidebar workspaceSlug={workspaceSlug} />
      <div className="flex min-w-0 flex-1 flex-col">
        <AppTopbar title={weddingName} eyebrow={formatWeddingDate(weddingDate)} />
        <main className="page-stagger-scope flex-1 overflow-y-auto px-9 py-8">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
